import { LivekitBaseController } from "./livekit_base_controller"
import { RoomEvent } from "livekit-client"

// ========================================
// LiveKit Streaming Controller
// ========================================
// This controller manages LiveKit-based voice recording and transcription
// using LiveKit's WebRTC infrastructure and Python agent
//
// Flow: User clicks mic → Connect to LiveKit room → Stream audio →
//       Python agent transcribes → Sends via data channel →
//       Updates chat input field in real-time
// ========================================

export default class extends LivekitBaseController {
  static targets = ["button", "stopButton", "statusText"]
  static values = {
    chatId: Number,
    language: String,
    livekitWsUrl: String
  }

  // ========================================
  // LIFECYCLE METHODS
  // ========================================
  connect() {
    this.initializeState()
  }

  disconnect() {
    this.cleanupResources()
  }

  initializeState() {
    // Recording state
    this.isRecording = false

    // Graceful shutdown state
    this.isShuttingDown = false
    this.shutdownTimeout = 3000  // 3 seconds max wait for final transcripts
    this.shutdownTimer = null

    // LiveKit room
    this.room = null

    // Text buffers for live streaming UX
    this.committedText = ""   // text confirmed by "final"
    this.partialText = ""     // current partial being shown

    // Get reference to input field
    this.inputField = document.getElementById("chat_message_input")
  }

  cleanupResources() {
    this.stopRecording()
  }

  // ========================================
  // USER INTERACTION
  // ========================================

  // Main entry point - user clicks the microphone button
  async toggleRecording() {
    if (this.isRecording) {
      this.handleStopRecording()
    } else {
      await this.handleStartRecording()
    }
  }

  // ========================================
  // RECORDING MANAGEMENT
  // ========================================

  async handleStartRecording() {
    try {
      // Step 1: Clear previous text
      this.committedText = ""
      this.partialText = ""
      if (this.inputField) {
        this.inputField.value = ""
      }

      // Step 2: Get LiveKit token from Rails
      this.updateUI("connecting")
      const tokenData = await this.fetchLiveKitToken({
        mode: "stt_only",
        language: this.languageValue || "en"
      })

      // Step 3: Connect to LiveKit room with STT-specific event handlers
      await this.connectToRoom(tokenData, {
        onConnected: () => {
          console.log('[LiveKit] Connected to room')
          this.updateUI("connected")
        },
        onDisconnected: () => {
          console.log('[LiveKit] Disconnected from room')
          this.updateUI("disconnected")
        },
        onReconnecting: () => {
          console.log('[LiveKit] Reconnecting...')
          this.updateUI("connecting")
        },
        onDataReceived: this.handleDataReceived.bind(this)
      })

      // Step 4: Get microphone access and publish
      await this.publishMicrophone()

      // Step 5: Update UI to show recording
      this.setRecordingState(true)

    } catch (error) {
      this.handleRecordingError(error)
    }
  }

  handleStopRecording() {
    if (!this.isRecording) return
    if (this.isShuttingDown) return  // Prevent double-click during shutdown

    this.isShuttingDown = true

    // Step 1: Update UI to show processing state
    this.updateUI("processing")

    // Step 2: Stop publishing NEW audio (but keep connection alive)
    this.stopPublishing()

    // Step 3: Send end-of-stream signal to agent
    this.sendEndSignal()

    // Step 4: Wait for agent's final transcripts with timeout
    this.waitForFinalTranscripts()
  }

  // ========================================
  // LIVEKIT EVENT HANDLERS
  // ========================================

  handleDataReceived(payload, participant, kind, topic) {
    console.log('[LiveKit] Data received:', { topic, kind, payloadSize: payload.byteLength })
    if (topic !== "stt") {
      console.log('[LiveKit] Ignoring non-stt topic:', topic)
      return
    }

    try {
      const text = new TextDecoder().decode(payload)
      const msg = JSON.parse(text)
      console.log('[LiveKit] STT message:', msg)

      if (msg.type === "partial") {
        this.partialText = msg.text || ""
        console.log('[LiveKit] Partial text:', this.partialText)
        this.renderInput()
      } else if (msg.type === "final") {
        const finalText = msg.text || ""
        console.log('[LiveKit] Final text:', finalText)
        // Commit final with a trailing space for fluid typing UX
        this.committedText += (this.committedText ? " " : "") + finalText
        this.partialText = ""
        this.renderInput()
      } else if (msg.type === "done") {
        console.log('[LiveKit] Agent finished processing, safe to disconnect')
        // Agent has sent all final transcripts, complete shutdown
        if (this.isShuttingDown) {
          this.completeShutdown()
        }
      }
    } catch (e) {
      console.error("[LiveKit] Bad stt payload", e)
    }
  }

  sendEndSignal() {
    if (!this.room) return
    try {
      const encoder = new TextEncoder()
      const data = encoder.encode(JSON.stringify({ type: "client_stopping" }))
      this.room.localParticipant.publishData(data, { reliable: true, topic: "control" })
      console.log('[LiveKit] Sent end-of-stream signal to agent')
    } catch (error) {
      console.error('[LiveKit] Failed to send end signal:', error)
    }
  }

  waitForFinalTranscripts() {
    // Set timeout to force disconnect after 3 seconds
    this.shutdownTimer = setTimeout(() => {
      console.log('[LiveKit] Shutdown timeout reached, forcing disconnect')
      this.completeShutdown()
    }, this.shutdownTimeout)

    console.log('[LiveKit] Waiting for final transcripts...')
  }

  completeShutdown() {
    // Clear timeout if still running
    if (this.shutdownTimer) {
      clearTimeout(this.shutdownTimer)
      this.shutdownTimer = null
    }

    // Reset shutdown flag
    this.isShuttingDown = false

    // Disconnect from room
    this.disconnectFromRoom()

    // Update UI to idle state
    this.setRecordingState(false)

    console.log('[LiveKit] Shutdown complete')
  }


  // ========================================
  // INPUT RENDERING (Streaming Text UX)
  // ========================================

  renderInput() {
    if (!this.inputField) return
    // Show committed + latest partial
    const combined = `${this.committedText}${this.partialText}`
    this.inputField.value = combined
    // Place cursor at end
    this.inputField.selectionStart = this.inputField.selectionEnd = combined.length
    console.log('[LiveKit] Rendered input:', combined)
  }

  // ========================================
  // UI UPDATES
  // ========================================

  setRecordingState(isRecording) {
    this.isRecording = isRecording
    this.updateUI(isRecording ? "recording" : "idle")
  }

  updateUI(status) {
    if (!this.hasButtonTarget) return

    const uiStates = {
      'idle': () => {
        this.showStreamButton()
        this.hideStopButton()
        this.hideStatusText()
      },
      'connecting': () => {
        this.hideStreamButton()
        this.showStopButton()
        this.showStatusText("Connecting to LiveKit...")
      },
      'connected': () => {
        this.showStatusText("Connected – Speak to dictate")
      },
      'recording': () => {
        this.hideStreamButton()
        this.showStopButton()
        this.showStatusText("Listening...")
      },
      'processing': () => {
        this.hideStreamButton()
        this.showStopButton()
        this.showStatusText("Processing final words...")
      },
      'disconnected': () => {
        this.showStreamButton()
        this.hideStopButton()
        this.hideStatusText()
      }
    }

    const stateHandler = uiStates[status]
    if (stateHandler) {
      stateHandler()
    }
  }

  showStreamButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.remove('hidden')
    }
  }

  hideStreamButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.add('hidden')
    }
  }

  showStopButton() {
    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.classList.remove('hidden')
    }
  }

  hideStopButton() {
    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.classList.add('hidden')
    }
  }

  showStatusText(message) {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = message
      this.statusTextTarget.classList.remove('hidden')
    }
  }

  hideStatusText() {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.classList.add('hidden')
    }
  }

  // ========================================
  // ERROR HANDLING
  // ========================================

  handleRecordingError(error) {
    console.error("Failed to start LiveKit recording:", error)
    this.showError("Failed to connect to LiveKit. Please check server.")
    this.setRecordingState(false)
  }

  showError(message) {
    console.error("LiveKit streaming error:", message)
    this.showStatusText(`Error: ${message}`)

    // Show as notification if Turbo is available
    if (window.Turbo) {
      const event = new CustomEvent("turbo:submit-end", {
        detail: {
          success: false,
          error: message
        }
      })
      document.dispatchEvent(event)
    }
  }

  // ========================================
  // HELPERS
  // ========================================

  // ========================================
  // PUBLIC API ALIASES
  // ========================================

  // Alias for public API
  startRecording() {
    return this.handleStartRecording()
  }

  stopRecording() {
    return this.handleStopRecording()
  }
}
